import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getMyBackgrounds, saveUiPreferences, type UiPreferences } from '../components/Background/backgrounds';
import { useAuth } from './AuthContext';

type BgType = 'none' | 'image' | 'video';
type BgState = { type: BgType; url?: string };

const LEGACY_BG_KEYS = ['ui:bg:main', 'ui:bg:side', 'ui:bg:prefs'];
const EMPTY_BG: BgState = { type: 'none' };

function bgKeysFor(userId: number | null) {
  if (userId == null) return null;
  return {
    main: `ui:bg:main:${userId}`,
    side: `ui:bg:side:${userId}`,
    prefs: `ui:bg:prefs:${userId}`,
  };
}

function readJson<T>(key: string | null, fallback: T): T {
  if (!key) return fallback;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function clearBackgroundLocalStorage() {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && (k.startsWith('ui:bg:main') || k.startsWith('ui:bg:side') || k.startsWith('ui:bg:prefs'))) {
        toRemove.push(k);
      }
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

type Ctx = {
  main: BgState;
  side: BgState;
  setMainImage: (url: string) => void;
  setMainVideo: (url: string) => void;
  clearMain: () => void;
  setSideImage: (url: string) => void;
  setSideVideo: (url: string) => void;
  clearSide: () => void;
  uiPreferences: UiPreferences;
  setUiPreferences: (prefs: UiPreferences) => Promise<void>;
};

const BackgroundContext = createContext<Ctx | null>(null);

export const BackgroundProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userId } = useAuth();
  const initialKeys = bgKeysFor(userId);

  const [main, setMain] = useState<BgState>(() => readJson<BgState>(initialKeys?.main ?? null, EMPTY_BG));
  const [side, setSide] = useState<BgState>(() => readJson<BgState>(initialKeys?.side ?? null, EMPTY_BG));
  const [uiPreferences, setUiPreferencesState] = useState<UiPreferences>(() => readJson<UiPreferences>(initialKeys?.prefs ?? null, {}));

  useEffect(() => {
    LEGACY_BG_KEYS.forEach((k) => {
      try { localStorage.removeItem(k); } catch { /* ignore */ }
    });
  }, []);

  const prevUserIdRef = useRef<number | null>(userId);
  useEffect(() => {
    if (prevUserIdRef.current === userId) return;
    prevUserIdRef.current = userId;
    const k = bgKeysFor(userId);
    setMain(readJson<BgState>(k?.main ?? null, EMPTY_BG));
    setSide(readJson<BgState>(k?.side ?? null, EMPTY_BG));
    setUiPreferencesState(readJson<UiPreferences>(k?.prefs ?? null, {}));
  }, [userId]);

  useEffect(() => {
    const k = bgKeysFor(userId);
    if (!k) return;
    try { localStorage.setItem(k.main, JSON.stringify(main)); } catch { /* ignore */ }
  }, [main, userId]);
  useEffect(() => {
    const k = bgKeysFor(userId);
    if (!k) return;
    try { localStorage.setItem(k.side, JSON.stringify(side)); } catch { /* ignore */ }
  }, [side, userId]);
  useEffect(() => {
    const k = bgKeysFor(userId);
    if (!k) return;
    try { localStorage.setItem(k.prefs, JSON.stringify(uiPreferences)); } catch { /* ignore */ }
  }, [uiPreferences, userId]);

  const inferTypeByExt = (url: string | null): BgType | null => {
    if (!url) return null;
    const u = url.split('?')[0].toLowerCase();
    const imgExt = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.avif', '.bmp', '.svg'];
    const vidExt = ['.mp4', '.webm', '.ogg', '.mov', '.m4v', '.avi', '.mkv'];
    if (imgExt.some((ext) => u.endsWith(ext))) return 'image';
    if (vidExt.some((ext) => u.endsWith(ext))) return 'video';
    return null;
  };

  useEffect(() => {
    let cancelled = false;

    if (userId == null) {
      setMain(EMPTY_BG);
      setSide(EMPTY_BG);
      setUiPreferencesState({});
      return () => { cancelled = true; };
    }

    (async () => {
      try {
        const payload = await getMyBackgrounds();
        const { mainUrl, sideUrl } = payload;
        if (cancelled) return;

        const mainType = inferTypeByExt(mainUrl);
        const sideType = inferTypeByExt(sideUrl);

        if (mainUrl && mainType) {
          setMain({ type: mainType, url: mainUrl });
        } else {
          setMain({ type: 'none' });
        }

        if (sideUrl && sideType) {
          setSide({ type: sideType, url: sideUrl });
        } else {
          setSide({ type: 'none' });
        }
        if (payload.uiPreferences) {
          setUiPreferencesState(payload.uiPreferences);
        }
      } catch {
        // ignore background load failures (e.g., unauthenticated)
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (main.type === 'image' && main.url) {
      document.body.style.backgroundImage = `url("${main.url}")`;
      document.body.style.backgroundSize = 'cover';
      document.body.style.backgroundRepeat = 'no-repeat';
      document.body.style.backgroundAttachment = 'fixed';
      document.body.style.backgroundPosition = 'center center';
    } else {
      document.body.style.backgroundImage = '';
    }
  }, [main]);

  const toRgba = (color: string, alpha: number) => {
    const a = Math.max(0, Math.min(1, alpha));
    const raw = (color || '').trim();
    const shortHex = /^#([0-9a-f]{3})$/i;
    const longHex = /^#([0-9a-f]{6})$/i;
    const rgb = /^rgb\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i;
    const rgba = /^rgba\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*([.\d]+)\s*\)$/i;

    const short = raw.match(shortHex);
    if (short) {
      const [r, g, b] = short[1].split('').map((v) => parseInt(v + v, 16));
      return `rgba(${r},${g},${b},${a})`;
    }
    const long = raw.match(longHex);
    if (long) {
      const v = long[1];
      const r = parseInt(v.slice(0, 2), 16);
      const g = parseInt(v.slice(2, 4), 16);
      const b = parseInt(v.slice(4, 6), 16);
      return `rgba(${r},${g},${b},${a})`;
    }
    const rgbMatch = raw.match(rgb);
    if (rgbMatch) {
      return `rgba(${rgbMatch[1]},${rgbMatch[2]},${rgbMatch[3]},${a})`;
    }
    const rgbaMatch = raw.match(rgba);
    if (rgbaMatch) {
      return `rgba(${rgbaMatch[1]},${rgbaMatch[2]},${rgbaMatch[3]},${a})`;
    }
    return raw || `rgba(31,41,55,${a})`;
  };

  // Глобальные переменные поверх фона: «стекло» для панелей; карточки всегда белые
  useEffect(() => {
    const root = document.documentElement;
    const hasAny = (main.type !== 'none' && main.url) || (side.type !== 'none' && side.url);
    const surfaceBase = uiPreferences.surfaceColor || '#ffffff';
    const sidebarSurfaceBase = uiPreferences.sidebarSurfaceColor || surfaceBase;
    if (!hasAny) {
      root.style.setProperty('--surface', toRgba(surfaceBase, 1));
      root.style.setProperty('--surface-muted', toRgba(surfaceBase, 0.94));
      root.style.setProperty('--surface-glass', toRgba(surfaceBase, 0.92));
      root.style.setProperty('--surface-glass-strong', toRgba(surfaceBase, 0.98));
      root.style.setProperty('--crm-card-surface', toRgba(surfaceBase, 1));
      root.style.setProperty('--crm-surface', toRgba(surfaceBase, 1));
      root.style.setProperty('--crm-surface-soft', toRgba(surfaceBase, 0.97));
      root.style.setProperty('--crm-sidebar-surface', toRgba(sidebarSurfaceBase, 0.96));
    } else {
      root.style.setProperty('--surface', toRgba(surfaceBase, 0.88));
      root.style.setProperty('--surface-muted', toRgba(surfaceBase, 0.75));
      root.style.setProperty('--surface-glass', toRgba(surfaceBase, 0.75));
      root.style.setProperty('--surface-glass-strong', toRgba(surfaceBase, 0.9));
      root.style.setProperty('--crm-card-surface', toRgba(surfaceBase, 0.88));
      root.style.setProperty('--crm-surface', toRgba(surfaceBase, 0.88));
      root.style.setProperty('--crm-surface-soft', toRgba(surfaceBase, 0.8));
      root.style.setProperty('--crm-sidebar-surface', toRgba(sidebarSurfaceBase, 0.9));
    }
    root.style.setProperty('--card-surface', '#ffffff');
    const textOpacityRaw = Number(uiPreferences.textOpacity);
    const textOpacity = Number.isFinite(textOpacityRaw) ? Math.min(1, Math.max(0.45, textOpacityRaw)) : 1;
    const mainTextColor = uiPreferences.textColor || '#1f2937';
    const sidebarTextColor = uiPreferences.sidebarTextColor || mainTextColor;
    root.style.setProperty('--crm-text', toRgba(mainTextColor, textOpacity));
    root.style.setProperty('--crm-sidebar-text', toRgba(sidebarTextColor, textOpacity));

    if (uiPreferences.surfaceOpacity) {
      const raw = Number(uiPreferences.surfaceOpacity);
      const clamped = Number.isFinite(raw) ? Math.min(1, Math.max(0.3, raw)) : 0.75;
      root.style.setProperty('--surface-glass', toRgba(surfaceBase, clamped));
      root.style.setProperty('--surface-glass-strong', toRgba(surfaceBase, Math.min(1, clamped + 0.1)));
      root.style.setProperty('--crm-card-surface', toRgba(surfaceBase, clamped));
      root.style.setProperty('--crm-surface', toRgba(surfaceBase, clamped));
      root.style.setProperty('--crm-surface-soft', toRgba(surfaceBase, Math.min(1, clamped + 0.08)));
      root.style.setProperty('--crm-sidebar-surface', toRgba(sidebarSurfaceBase, clamped));
    }
    if (uiPreferences.surfaceBlur) {
      root.style.setProperty('--crm-surface-blur', uiPreferences.surfaceBlur);
    } else {
      root.style.setProperty('--crm-surface-blur', '8px');
    }
  }, [main, side, uiPreferences]);

  const persistUiPreferences = async (prefs: UiPreferences) => {
    setUiPreferencesState(prefs);
    try {
      const payload = await saveUiPreferences(prefs);
      if (payload.uiPreferences) {
        setUiPreferencesState(payload.uiPreferences);
      }
    } catch (err) {
      console.warn('Failed to persist UI preferences', err);
    }
  };

  const value = useMemo((): Ctx => ({
    main, side,
    setMainImage: (url) => setMain({ type: 'image', url }),
    setMainVideo: (url) => setMain({ type: 'video', url }),
    clearMain: () => setMain({ type: 'none' }),
    setSideImage: (url) => setSide({ type: 'image', url }),
    setSideVideo: (url) => setSide({ type: 'video', url }),
    clearSide: () => setSide({ type: 'none' }),
    uiPreferences,
    setUiPreferences: persistUiPreferences,
  }), [main, side, uiPreferences]);

  return <BackgroundContext.Provider value={value}>{children}</BackgroundContext.Provider>;
};

export const useBackground = () => {
  const ctx = useContext(BackgroundContext);
  if (!ctx) throw new Error('useBackground must be used inside BackgroundProvider');
  return ctx;
};
